import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Paper, Grid, Stack, TextField, MenuItem, Button, Chip, Divider, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Alert } from '@mui/material';
import dayjs from 'dayjs';
import { apiService } from '../../services/apiService';

const perspectives = ['neutral','progressive','conservative','libertarian','unknown'];
const platforms = ['x','twitter','facebook','instagram','reddit','generic','unknown'];

const AdminCivicResponse: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<{ q?: string; perspective?: string; platform?: string; start?: string; end?: string }>({});
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(0);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, l] = await Promise.all([
        apiService.getCivicResponseMetrics(14),
        apiService.listCivicResponseQueries({ ...filters, limit, offset: page * limit })
      ]);
      setMetrics(m.data || null);
      setItems(Array.isArray(l.data?.items) ? l.data.items : []);
      setTotal(Number(l.data?.total || 0));
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load Civic Response analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [page, limit]);

  const onApplyFilters = () => { setPage(0); reload(); };

  const onModerationUpdate = async (id: number, flag: string) => {
    setUpdatingId(id);
    try {
      await apiService.updateCivicResponseModeration(id, { moderation_flag: flag });
      await reload();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to update moderation');
    } finally {
      setUpdatingId(null);
    }
  };

  const series = (metrics?.series || []) as Array<{ date: string; count: number }>;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Civic Response Studio — Admin & Analytics</Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Monitor usage and review generated responses. You can search, filter by perspective or platform, and flag entries.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Metrics */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Daily requests (last {metrics?.days || 14} days)</Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 140 }}>
              {series.map((pt, idx) => (
                <Box key={idx} sx={{ width: `${100/Math.max(1, series.length)}%`, bgcolor: 'primary.main', height: `${Math.min(100, (pt.count || 0) * 10)}%` }} title={`${pt.date}: ${pt.count}`} />
              ))}
            </Box>
            <Typography variant="caption" color="textSecondary">Total: {metrics?.total || 0}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Perspectives</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {Object.entries(metrics?.perspective || {}).map(([k,v]: any) => (
                <Chip key={k} label={`${k}: ${v}`} />
              ))}
            </Stack>
          </Paper>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Platforms</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {Object.entries(metrics?.platform || {}).map(([k,v]: any) => (
                <Chip key={k} label={`${k}: ${v}`} />
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField label="Search" value={filters.q || ''} onChange={(e)=> setFilters(prev=>({ ...prev, q: e.target.value }))} fullWidth />
          <TextField select label="Perspective" value={filters.perspective || ''} onChange={(e)=> setFilters(prev=>({ ...prev, perspective: e.target.value }))} fullWidth>
            <MenuItem value="">Any</MenuItem>
            {perspectives.map(p => (<MenuItem key={p} value={p}>{p}</MenuItem>))}
          </TextField>
          <TextField select label="Platform" value={filters.platform || ''} onChange={(e)=> setFilters(prev=>({ ...prev, platform: e.target.value }))} fullWidth>
            <MenuItem value="">Any</MenuItem>
            {platforms.map(p => (<MenuItem key={p} value={p}>{p}</MenuItem>))}
          </TextField>
          <TextField type="date" label="Start" InputLabelProps={{ shrink: true }} value={filters.start || ''} onChange={(e)=> setFilters(prev=>({ ...prev, start: e.target.value }))} />
          <TextField type="date" label="End" InputLabelProps={{ shrink: true }} value={filters.end || ''} onChange={(e)=> setFilters(prev=>({ ...prev, end: e.target.value }))} />
          <Button variant="contained" onClick={onApplyFilters}>Apply</Button>
        </Stack>
      </Paper>

      {/* Table */}
      <Paper sx={{ p: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Perspective</TableCell>
              <TableCell>Platform</TableCell>
              <TableCell>Original Comment</TableCell>
              <TableCell>Reply</TableCell>
              <TableCell>Moderation</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((it) => (
              <TableRow key={it.id} hover>
                <TableCell>{it.created_at ? dayjs(it.created_at).format('YYYY-MM-DD HH:mm') : ''}</TableCell>
                <TableCell><Chip size="small" label={it.perspective || 'unknown'} /></TableCell>
                <TableCell><Chip size="small" label={it.platform || 'unknown'} /></TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{it.original_comment}</Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{it.latest_answer?.social_reply || it.latest_answer?.explanation || ''}</Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <Chip size="small" color={it.moderation_flag === 'blocked' ? 'error' : (it.moderation_flag === 'hidden' ? 'default' : 'success')} label={it.moderation_flag || 'allowed'} />
                    <Button size="small" variant="outlined" disabled={updatingId === it.id} onClick={() => onModerationUpdate(it.id, it.moderation_flag === 'hidden' ? 'allowed' : 'hidden')}>
                      {it.moderation_flag === 'hidden' ? 'Unhide' : 'Hide'}
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && !loading && (
              <TableRow><TableCell colSpan={6}><Typography variant="body2" color="textSecondary" align="center">No items</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={limit}
          onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10,20,50,100]}
        />
      </Paper>
    </Box>
  );
};

export default AdminCivicResponse;
